import type { User } from 'firebase/auth';
import type { ComponentProps } from 'react';
import * as Sentry from '@sentry/nextjs';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import React from 'react';
import { z } from 'zod';

import { type RouterOutputs } from '@poseidon/api/types';

import { api } from '@/utils/api';
import isClientSide from './isClientSide';
import { isImpersonationTokenExpired } from './impersonationToken';

export const IMPERSONATION_KEY = 'impersonation_session';
/** sessionStorage flag: set right before a reload when the server reports an
 * expired impersonation token, so a toast can be shown after the reload. */
export const IMPERSONATION_EXPIRED_FLAG = 'impersonation_expired_toast';

interface ImpersonationSession {
  token: string;
  email: string;
  userId: string;
  /** Profile picture of the impersonated user */
  profilePicture?: string | null;
  /** The superadmin's name (e.g., first name) */
  superadminName?: string;
  /** The superadmin's team name */
  superadminTeamName?: string;
}

export type PoseidonApiAuthRes = NonNullable<RouterOutputs['user']['me']>;

export type AuthContextProps = [
  (
    | PoseidonApiAuthRes
    | {
        user: null;
      }
  ) & {
    isPending: boolean;
    isError: boolean;
    impersonation: ImpersonationSession | null;
  },
  {
    startImpersonation: (
      token: string,
      email: string,
      userId: string,
      superadminInfo?: { name?: string; teamName?: string },
      profilePicture?: string | null,
    ) => void;
    stopImpersonation: () => void;
  },
];

const defaultValue: AuthContextProps = [
  {
    user: null,
    isPending: true,
    isError: false,
    impersonation: null,
  },
  {
    startImpersonation: () => {
      throw new Error('AuthProvider not initialized');
    },
    stopImpersonation: () => {
      throw new Error('AuthProvider not initialized');
    },
  },
];

const AuthContext = React.createContext<AuthContextProps>(defaultValue);

export const AuthProvider = ({ children }: ComponentProps<'div'>) => {
  const [firebaseUser, setFirebaseUser] = React.useState<User | null>();
  const [impersonationSession, setImpersonationSession] = React.useState<ImpersonationSession | null>(null);
  const [isTransitioning, setIsTransitioning] = React.useState(false);

  const {
    data,
    isPending,
    isError,
    refetch: refetchUser,
  } = api.user.me.useQuery(undefined, { enabled: Boolean(firebaseUser) });

  React.useEffect(() => {
    if (isClientSide()) {
      const unsubscribe = onAuthStateChanged(getAuth(), (u) => {
        if (u == null) {
          Sentry.setUser(null);
          Sentry.setTag('user_email', undefined);
        } else {
          const sUser = { id: u.uid, email: u.email ?? undefined };
          Sentry.setUser(sUser);
          Sentry.setTag('user_email', u.email ?? undefined);
        }

        setFirebaseUser(u);
        // Force refetch user.me when auth state changes to avoid serving
        // stale cached data (e.g. from a previous session with user: null).
        if (u) {
          void refetchUser();
        }
      });
      return () => unsubscribe();
    }
  }, [setFirebaseUser, refetchUser]);

  React.useEffect(() => {
    const sessionData = impersonationSessionGet();
    setImpersonationSession(sessionData);
  }, []);

  React.useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === IMPERSONATION_KEY) {
        setIsTransitioning(true);

        if (event.newValue) {
          try {
            const sessionData = JSON.parse(event.newValue) as unknown;

            const schema = z
              .object({
                token: z.string(),
                email: z.string(),
                userId: z.string(),
                profilePicture: z.string().nullish(),
                superadminName: z.string().optional(),
                superadminTeamName: z.string().optional(),
              })
              .nullish();
            const parsed = schema.parse(sessionData);

            setImpersonationSession(parsed ?? null);
          } catch {
            setImpersonationSession(null);
          }
        } else {
          setImpersonationSession(null);
        }

        refetchUser()
          .catch(() => {
            // do nothing
          })
          .finally(() => {
            setIsTransitioning(false);
          });
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [refetchUser]);

  const startImpersonation = React.useCallback(
    (token: string, email: string, userId: string, superadminInfo?: { name?: string; teamName?: string }, profilePicture?: string | null) => {
      setIsTransitioning(true);
      const newSession: ImpersonationSession = {
        token,
        email,
        userId,
        profilePicture,
        superadminName: superadminInfo?.name,
        superadminTeamName: superadminInfo?.teamName,
      };
      localStorage.setItem(IMPERSONATION_KEY, JSON.stringify(newSession));
      setImpersonationSession(newSession);

      refetchUser()
        .catch(() => {
          // do nothing
        })
        .finally(() => {
          setIsTransitioning(false);
        });
    },
    [refetchUser],
  );

  const stopImpersonation = React.useCallback(() => {
    setIsTransitioning(true);
    localStorage.removeItem(IMPERSONATION_KEY);
    setImpersonationSession(null);

    refetchUser()
      .catch(() => {
        // do nothing
      })
      .finally(() => {
        setIsTransitioning(false);
      });
  }, [refetchUser]);

  const authData = data == null ? { user: null } : data;

  const userObj = isTransitioning ? { user: null } : authData;

  const value: AuthContextProps = [
    {
      isPending: (firebaseUser === null ? false : isPending) || isTransitioning,
      isError,
      impersonation: impersonationSession,
      ...userObj,
    },
    { startImpersonation, stopImpersonation },
  ];

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => React.useContext(AuthContext);

export function impersonationSessionGet(): ImpersonationSession | null {
  if (typeof window === 'undefined') return null;
  const impersonationSessionStr = localStorage.getItem(IMPERSONATION_KEY);
  if (!impersonationSessionStr) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(impersonationSessionStr);
  } catch {
    // bad value, clean it
    localStorage.removeItem(IMPERSONATION_KEY);
    return null;
  }

  const schema = z
    .object({
      token: z.string(),
      email: z.string(),
      userId: z.string(),
      profilePicture: z.string().nullish(),
      superadminName: z.string().optional(),
      superadminTeamName: z.string().optional(),
    })
    .nullish();

  const parseRes = schema.safeParse(parsed);
  if (!parseRes.success) {
    // bad value or old format without userId, clean it
    localStorage.removeItem(IMPERSONATION_KEY);
    return null;
  }

  const session = parseRes.data ?? null;

  // Drop an expired session before it can be sent. Otherwise the server rejects
  // the stale token and silently reverts to the real admin's identity, while the
  // impersonation banner (localStorage-driven) keeps showing.
  if (session && isImpersonationTokenExpired(session.token)) {
    localStorage.removeItem(IMPERSONATION_KEY);
    return null;
  }

  return session;
}
